import { ShieldCheckIcon, ShieldWarningIcon } from '@phosphor-icons/react';
import { useGT } from 'gt-react';
import {
  validationMarkState,
  type columnValidationStatus,
} from './worksheetValidations';

type Status = NonNullable<ReturnType<typeof columnValidationStatus>>;

const ICONS = {
  error: ShieldWarningIcon,
  warning: ShieldWarningIcon,
  passed: ShieldCheckIcon,
  pending: ShieldCheckIcon,
} satisfies Record<Status['outcome'], unknown>;
const TONES: Record<Status['outcome'], string> = {
  error: 'text-destructive',
  warning: 'text-amber-600 dark:text-amber-400',
  passed: 'text-green-700 dark:text-green-400',
  pending: 'text-muted-foreground',
};

/** A column's Validations at a glance: a shield, colored by the worst outcome. */
export function ColumnValidationMark({ status }: { status: Status | null }) {
  const gt = useGT();
  if (!status) return null;
  const { says, one } = validationMarkState(status);
  const labels = {
    failing: one
      ? gt('Validation failing', {
          context: 'worksheet column validation indicator',
        })
      : gt('{count} validations, {failing} failing', {
          context: 'worksheet column validation indicator',
          count: status.count,
          failing: status.failingRules,
        }),
    passed: one
      ? gt('Validation passed', {
          context: 'worksheet column validation indicator',
        })
      : gt('{count} validations, all passed', {
          context: 'worksheet column validation indicator',
          count: status.count,
        }),
    pending: one
      ? gt('Validation not checked yet', {
          context: 'worksheet column validation indicator',
        })
      : gt('{count} validations, not checked yet', {
          context: 'worksheet column validation indicator',
          count: status.count,
        }),
  };
  const Icon = ICONS[status.outcome];
  return (
    <Icon
      size={14}
      role="img"
      aria-label={labels[says]}
      className={TONES[status.outcome]}
    >
      <title>{labels[says]}</title>
    </Icon>
  );
}
